import cx from 'classnames';
import React, { InputHTMLAttributes, JSX, forwardRef, useMemo } from 'react';
import { FieldPath, RegisterOptions, useFormContext } from 'react-hook-form';
import Text from '../../Text/Text';
import styles from './Input.module.scss';

export interface InputProps<
  TFieldValues extends Record<string, unknown> = Record<string, unknown>,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  name?: TName;
  registerOptions?: RegisterOptions<TFieldValues, TName>;
  iconLeft?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  iconRight?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  label?: string;
  labelPosition?: 'top' | 'sideLeft' | 'sideRight' | 'none';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      name,
      iconLeft: IconLeft,
      iconRight: IconRight,
      registerOptions = {},
      label,
      labelPosition = 'top',
      ...props
    },
    ref,
  ) => {
    const { register, formState, getFieldState } = useFormContext();
    const errorMsg = useMemo(
      () => (name ? getFieldState(name, formState)?.error?.message : undefined),
      [name, getFieldState, formState],
    );

    const wrapperClass = cx(styles.inputWrapper, {
      [styles.labelTop]: label && labelPosition === 'top',
      [styles.labelSideLeft]: label && labelPosition === 'sideLeft',
      [styles.labelSideRight]: label && labelPosition === 'sideRight',
      [styles.labelNone]: !label && labelPosition === 'none',
    });

    return (
      <div data-testid="input-container" className={cx(wrapperClass, className)}>
        {label && labelPosition !== 'none' && <Text>{label}</Text>}
        <div className={cx(styles.input, IconLeft && styles.withIconLeft, IconRight && styles.withIconRight)}>
          {IconLeft && (
            <div className={cx(styles.iconWrapper, styles.left)}>
              <IconLeft height="2rem" />
            </div>
          )}

          <input
            ref={ref}
            {...(props.value !== undefined || !name ? props : { ...register(name, registerOptions), ...props })}
          />

          {IconRight && (
            <div className={cx(styles.iconWrapper, styles.right)}>
              <IconRight height="2rem" />
            </div>
          )}

          {errorMsg && <p className={styles.error}>{errorMsg}</p>}
        </div>
      </div>
    );
  },
);

export default Input;
